<template>
  <div class="mobile-build-controller">
    <transition name="mobile-build-fade">
      <button
        v-if="drawerOpen"
        type="button"
        class="mobile-build-backdrop"
        aria-label="Close build checklist"
        @click="closeDrawer"
      ></button>
    </transition>

    <transition name="mobile-build-sheet">
      <section
        v-if="drawerOpen"
        class="mobile-build-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Build checklist"
      >
        <div class="mobile-build-sheet__handle" aria-hidden="true"></div>
        <div class="mobile-build-sheet__header">
          <div>
            <div class="mobile-build-sheet__eyebrow">Build checklist</div>
            <div class="mobile-build-sheet__title">
              {{ filledSlotsCount }}/{{ totalSlots }} slots selected
            </div>
          </div>
          <button type="button" class="mobile-build-sheet__close" aria-label="Close build checklist" @click="closeDrawer">
            <span class="icon icon-cross"></span>
          </button>
        </div>
        <div class="mobile-build-sheet__status" :class="'mobile-build-sheet__status--' + statusTone">
          <span class="icon" :class="statusIcon"></span>
          <span>{{ statusLabel }}</span>
        </div>
        <div class="mobile-build-sheet__content">
          <Menu />
        </div>
      </section>
    </transition>

    <div class="mobile-build-dock" role="navigation" aria-label="Mobile build summary">
      <button type="button" class="mobile-build-dock__summary" @click="toggleDrawer">
        <span class="mobile-build-dock__label">Build</span>
        <span class="mobile-build-dock__detail">{{ filledSlotsCount }}/{{ totalSlots }} selected</span>
      </button>
      <div v-if="hasKnownTotal" class="mobile-build-dock__price" :aria-label="showPrices ? 'Build total' : 'Estimated build total'">
        <span class="mobile-build-dock__price-label">{{ showPrices ? 'Total' : 'Est.' }}</span>
        <span v-if="showPrices" class="mobile-build-price-lockup">
          <span class="mobile-build-price-currency">$</span>
          <span class="mobile-build-price-whole">{{ totalPriceWhole }}</span>
          <span class="mobile-build-price-cents">.{{ totalPriceCents }}</span>
        </span>
        <span v-else class="mobile-build-price-lockup mobile-build-price-range">{{ typicalTotalRange }}</span>
      </div>
      <div v-else class="mobile-build-dock__price" aria-label="Build status">
        <span class="mobile-build-dock__price-label">Fit</span>
        <span class="mobile-build-price-lockup mobile-build-fit-copy">{{ statusShortLabel }}</span>
      </div>
      <button type="button" class="mobile-build-dock__open" @click="toggleDrawer">
        <span class="icon" :class="drawerOpen ? 'icon-chevron-thin-down' : 'icon-chevron-thin-up'"></span>
        <span>{{ drawerOpen ? 'Close' : 'Checklist' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Menu from './Menu.vue'
import { BUILD_PARTS } from '../../partOrder'
import { SHOW_PRICES } from '../../config'
import { EventBus } from '../../eventBus'
import { functions } from '../../functions'

export default {
  name: 'MobileBuildDock',
  components: {
    Menu,
  },
  data() {
    return {
      drawerOpen: false,
      totalSlots: BUILD_PARTS.length,
    }
  },
  computed: {
    ...mapGetters([
      'allMenuOptions',
      'getBuidStatus',
      'getWarnings',
      'getTotalCost',
    ]),
    filledSlotsCount() {
      var options = this.allMenuOptions || {}
      var keys = Object.keys(options)
      var count = 0

      for(var i = 0; i < keys.length; i++){
        var item = options[keys[i]] && options[keys[i]].item
        var contents = item ? item.contents : null
        if(Array.isArray(contents) && contents.length > 0){
          count++
        }
        else if(contents !== null && contents !== undefined && !Array.isArray(contents)){
          count++
        }
      }

      return count
    },
    hasSelectedParts() {
      return this.filledSlotsCount > 0
    },
    showPrices() {
      return SHOW_PRICES
    },
    normalizedTotalPrice() {
      var value = parseFloat(this.getTotalCost)
      if(isNaN(value)){
        return 0
      }
      return value
    },
    hasKnownTotal() {
      return this.normalizedTotalPrice > 0
    },
    typicalTotalRange() {
      return functions.formatTypicalPriceBand(this.normalizedTotalPrice)
    },
    roundedTotalCents() {
      return Math.round(this.normalizedTotalPrice * 100)
    },
    totalPriceWhole() {
      return Math.floor(this.roundedTotalCents / 100)
    },
    totalPriceCents() {
      var cents = this.roundedTotalCents % 100
      return cents < 10 ? '0' + cents : String(cents)
    },
    statusTone() {
      if(!this.hasSelectedParts){
        return 'idle'
      }
      if(this.getBuidStatus == 'sad'){
        return 'danger'
      }
      if(this.getBuidStatus == 'warning'){
        return 'warning'
      }
      return 'ok'
    },
    statusIcon() {
      if(this.statusTone == 'danger'){
        return 'icon-circle-with-cross'
      }
      if(this.statusTone == 'warning'){
        return 'icon-warning'
      }
      if(this.statusTone == 'ok'){
        return 'icon-check'
      }
      return 'icon-info-with-circle'
    },
    statusLabel() {
      if(!this.hasSelectedParts){
        return 'Start with a processor, then add the parts that define the build.'
      }
      if(this.getBuidStatus == 'sad'){
        return 'Compatibility conflict found. Review highlighted parts.'
      }
      if(this.getBuidStatus == 'warning'){
        if(this.getWarnings && this.getWarnings.length){
          return this.getWarnings[0]
        }
        return 'Compatibility warning needs review.'
      }
      return 'Selected parts are compatible so far.'
    },
    statusShortLabel() {
      if(!this.hasSelectedParts){
        return 'Ready'
      }
      if(this.statusTone == 'danger'){
        return 'Review'
      }
      if(this.statusTone == 'warning'){
        return 'Warning'
      }
      return 'OK'
    },
  },
  watch: {
    '$route.fullPath': function() {
      this.closeDrawer()
    },
    drawerOpen: function(isOpen) {
      if(typeof document === 'undefined'){
        return
      }
      document.body.classList.toggle('mobile-build-drawer-open', isOpen)
    },
  },
  methods: {
    toggleDrawer() {
      this.drawerOpen = !this.drawerOpen
    },
    closeDrawer() {
      this.drawerOpen = false
    },
    openSavePrompt(context) {
      this.drawerOpen = true
      this.$nextTick(() => {
        EventBus.$emit('open-mobile-build-save', context)
      })
    },
  },
  mounted() {
    EventBus.$on('request-build-save', this.openSavePrompt)
  },
  beforeDestroy() {
    EventBus.$off('request-build-save', this.openSavePrompt)
    if(typeof document !== 'undefined'){
      document.body.classList.remove('mobile-build-drawer-open')
    }
  },
}
</script>

<style>
.mobile-build-controller {
  display: none;
}

@media (max-width: 760px) {
  body.mobile-build-drawer-open {
    overflow: hidden;
  }

  .mobile-build-controller {
    display: block;
  }

  .mobile-build-backdrop {
    position: fixed;
    inset: 0;
    z-index: 9990;
    border: 0;
    background: rgba(1, 4, 9, 0.64);
    backdrop-filter: blur(3px);
  }

  .mobile-build-dock {
    position: fixed;
    right: 10px;
    bottom: 10px;
    left: 10px;
    z-index: 9985;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(82px, auto) auto;
    align-items: center;
    gap: 0.55rem;
    min-height: 62px;
    padding: 0.52rem;
    border: 1px solid rgba(25, 151, 198, 0.52);
    border-radius: 8px;
    background:
      radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.16), rgba(25, 151, 198, 0) 38%),
      rgba(8, 11, 17, 0.94);
    box-shadow:
      0 -18px 42px rgba(0, 0, 0, 0.44),
      inset 0 1px 0 rgba(255, 255, 255, 0.045);
    backdrop-filter: blur(12px);
  }

  .mobile-build-dock__summary,
  .mobile-build-dock__open {
    min-width: 0;
    border: 1px solid rgba(67, 72, 87, 0.78);
    border-radius: 7px;
    background: rgba(12, 16, 24, 0.72);
    color: #cfd2da;
  }

  .mobile-build-dock__summary {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    min-height: 46px;
    padding: 0 0.72rem;
    text-align: left;
  }

  .mobile-build-dock__label {
    color: #1fb2e8;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    line-height: 1;
    text-transform: uppercase;
  }

  .mobile-build-dock__detail {
    margin-top: 0.28rem;
    color: #9ca6b8;
    font-size: 12px;
    line-height: 1;
  }

  .mobile-build-dock__price {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    min-width: 86px;
    min-height: 46px;
    padding: 0 0.35rem;
  }

  .mobile-build-dock__price-label {
    margin-bottom: 0.18rem;
    color: #747d90;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    line-height: 1;
    text-transform: uppercase;
  }

  .mobile-build-price-lockup {
    display: inline-flex;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 0.12rem;
    color: #e4d836;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    white-space: nowrap;
  }

  .mobile-build-price-currency {
    margin-top: 0.1rem;
    color: #9198aa;
    font-size: 13px;
    line-height: 1;
  }

  .mobile-build-price-whole {
    color: #e4d836;
    font-size: 21px;
    font-weight: 500;
    letter-spacing: 0;
  }

  .mobile-build-price-cents {
    margin-top: 0.14rem;
    color: #e4d836;
    font-size: 12px;
    font-weight: 700;
  }

  .mobile-build-price-range {
    color: #e4d836;
    font-size: 13px;
    font-weight: 750;
    letter-spacing: -0.01em;
  }

  .mobile-build-fit-copy {
    color: #cfd2da;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .mobile-build-dock__open {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    min-height: 46px;
    padding: 0 0.72rem;
    color: #1fb2e8;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  .mobile-build-dock__summary:focus,
  .mobile-build-dock__summary:hover,
  .mobile-build-dock__open:focus,
  .mobile-build-dock__open:hover {
    border-color: rgba(25, 151, 198, 0.88);
    background: rgba(25, 151, 198, 0.12);
    color: #eff7fb;
    outline: none;
  }

  .mobile-build-sheet {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 9995;
    display: flex;
    flex-direction: column;
    max-height: min(86vh, 720px);
    padding: 0.62rem 0.78rem calc(78px + env(safe-area-inset-bottom, 0px));
    border: 1px solid rgba(74, 82, 103, 0.92);
    border-bottom: 0;
    border-radius: 14px 14px 0 0;
    background:
      radial-gradient(circle at 50% 0%, rgba(25, 151, 198, 0.17), rgba(25, 151, 198, 0) 35%),
      linear-gradient(180deg, rgba(14, 18, 27, 0.98), rgba(5, 8, 13, 0.98));
    box-shadow: 0 -28px 60px rgba(0, 0, 0, 0.58);
    color: #cfd2da;
  }

  .mobile-build-sheet__handle {
    width: 42px;
    height: 4px;
    margin: 0 auto 0.72rem;
    border-radius: 999px;
    background: rgba(145, 152, 170, 0.5);
  }

  .mobile-build-sheet__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 0.7rem;
    border-bottom: 1px solid rgba(67, 72, 87, 0.72);
  }

  .mobile-build-sheet__eyebrow {
    margin-bottom: 0.18rem;
    color: #1997c6;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .mobile-build-sheet__title {
    color: #dce2ec;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.15;
  }

  .mobile-build-sheet__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: 1px solid rgba(67, 72, 87, 0.82);
    border-radius: 8px;
    background: rgba(8, 11, 17, 0.74);
    color: #9ca6b8;
  }

  .mobile-build-sheet__close:hover,
  .mobile-build-sheet__close:focus {
    border-color: rgba(25, 151, 198, 0.75);
    color: #f0f6fb;
    outline: none;
  }

  .mobile-build-sheet__status {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    margin-top: 0.72rem;
    padding: 0.68rem 0.74rem;
    border: 1px solid rgba(67, 72, 87, 0.7);
    border-radius: 8px;
    background: rgba(8, 11, 17, 0.48);
    color: #aeb6c6;
    font-size: 12px;
    line-height: 1.38;
  }

  .mobile-build-sheet__status .icon {
    flex: 0 0 auto;
    margin-top: 1px;
  }

  .mobile-build-sheet__status--ok {
    border-color: rgba(0, 188, 140, 0.42);
    background: rgba(0, 188, 140, 0.08);
    color: #b9f1df;
  }

  .mobile-build-sheet__status--warning {
    border-color: rgba(207, 176, 39, 0.5);
    background: rgba(207, 176, 39, 0.08);
    color: #f0dda0;
  }

  .mobile-build-sheet__status--danger {
    border-color: rgba(230, 71, 89, 0.52);
    background: rgba(230, 71, 89, 0.08);
    color: #ffc7cf;
  }

  .mobile-build-sheet__content {
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-top: 0.75rem;
    scrollbar-width: thin;
    scrollbar-color: rgba(25, 151, 198, 0.42) rgba(18, 22, 31, 0.62);
  }

  .mobile-build-sheet .build-checklist {
    width: 100%;
    max-width: none;
    max-height: none;
    margin: 0 !important;
    padding: 0 0 0.3rem !important;
    border-right: 0 !important;
    overflow: visible;
  }

  .mobile-build-sheet .build-checklist::before,
  .mobile-build-sheet .build-checklist .menu-label {
    display: none;
  }

  .mobile-build-sheet .build-checklist .menu-option {
    min-height: 44px;
    margin-bottom: 0.48rem;
  }

  .mobile-build-sheet .build-checklist .item-label,
  .mobile-build-sheet .build-checklist .item-label.selected {
    max-width: min(48vw, 190px);
  }

  .mobile-build-sheet .build-checklist .build-summary {
    margin-top: 0.62rem;
  }

  .mobile-build-sheet .build-checklist .build-metrics {
    margin: 0.78rem 0 0.88rem;
  }

  .mobile-build-sheet .build-actions {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.48rem;
  }

  .mobile-build-fade-enter-active,
  .mobile-build-fade-leave-active {
    transition: opacity 180ms ease;
  }

  .mobile-build-fade-enter,
  .mobile-build-fade-leave-to {
    opacity: 0;
  }

  .mobile-build-sheet-enter-active,
  .mobile-build-sheet-leave-active {
    transition: transform 220ms ease, opacity 180ms ease;
  }

  .mobile-build-sheet-enter,
  .mobile-build-sheet-leave-to {
    opacity: 0;
    transform: translateY(18px);
  }
}

@media (min-width: 641px) and (max-width: 760px) {
  .mobile-build-controller {
    padding: 0 10px calc(10px + env(safe-area-inset-bottom, 0px));
  }

  .mobile-build-dock {
    position: static;
    right: auto;
    bottom: auto;
    left: auto;
    margin-top: 12px;
  }
}

@media (max-width: 420px) {
  .mobile-build-dock {
    grid-template-columns: minmax(0, 1fr) minmax(56px, auto) auto;
    gap: 0.42rem;
    right: 8px;
    left: 8px;
    padding: 0.42rem;
  }

  .mobile-build-dock__summary {
    padding: 0 0.58rem;
  }

  .mobile-build-dock__summary,
  .mobile-build-dock__open {
    min-height: 44px;
  }

  .mobile-build-dock__price {
    min-width: 56px;
    padding: 0 0.16rem;
  }

  .mobile-build-price-whole {
    font-size: 18px;
  }

  .mobile-build-price-cents {
    font-size: 10px;
  }

  .mobile-build-price-range {
    font-size: 11px;
  }

  .mobile-build-dock__open {
    padding: 0 0.55rem;
    font-size: 11px;
  }

  .mobile-build-sheet {
    padding-right: 0.62rem;
    padding-left: 0.62rem;
  }

  .mobile-build-sheet .build-actions {
    gap: 0.38rem;
  }

  .mobile-build-sheet .build-checklist .gc-button {
    padding: 0 0.45rem;
    font-size: 11px;
  }
}
</style>
